import React, { useEffect, useState } from 'react';
import { Drawer, Box, Typography, TextField, Button, IconButton, FormControl, InputLabel, Select, MenuItem, FormHelperText, useTheme, useMediaQuery, InputAdornment, CircularProgress, Tooltip, Alert, Fade, Autocomplete, ListItemText, Paper, InputBase, Divider } from '@mui/material';
import { XIcon, AlertCircleIcon, CheckCircleIcon, SearchIcon } from 'lucide-react';
import { VALID_SUB_AGENTS, type ValidSubAgent } from '../utils/constants';
// International phone format for validation
const PHONE_FORMAT = {
  placeholder: '+233 20 123 4567',
  helperText: 'International format, e.g. +233 20 123 4567',
  regex: /^\+\d{7,15}$/
};
interface SubAgentFormProps {
  open: boolean;
  subAgent: any | null;
  onClose: () => void;
  onSave: (subAgentData: any) => void;
}
interface FormData {
  name: ValidSubAgent | string;
  description: string;
  headOfficeAddress: string;
  contactPerson: string;
  contactPhone: string;
  contactEmail: string;
  settlementAccount: string;
  accountTitle?: string;
}
interface FormErrors {
  name?: string;
  description?: string;
  headOfficeAddress?: string;
  contactPerson?: string;
  contactPhone?: string;
  contactEmail?: string;
  settlementAccount?: string;
}
// Email validation regex
const EMAIL_REGEX = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
// IBAN validation regex
const GENERAL_IBAN_REGEX = /^[A-Z]{2}\d{2}[A-Z0-9]{11,30}$/;
const SubAgentFormDrawer: React.FC<SubAgentFormProps> = ({
  open,
  subAgent,
  onClose,
  onSave
}) => {
  const [formData, setFormData] = useState<FormData>({
    name: '',
    description: '',
    headOfficeAddress: '',
    contactPerson: '',
    contactPhone: '',
    contactEmail: '',
    settlementAccount: ''
  });
  const [errors, setErrors] = useState<FormErrors>({});
  const [isFetchingTitle, setIsFetchingTitle] = useState(false);
  const [fetchError, setFetchError] = useState<string | null>(null);
  const [showAccountTitle, setShowAccountTitle] = useState(false);
  const [ibanValid, setIbanValid] = useState(false);
  const [phoneFieldFocused, setPhoneFieldFocused] = useState(false);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  useEffect(() => {
    if (subAgent) {
      setFormData({
        name: subAgent.name || '',
        description: subAgent.description || '',
        headOfficeAddress: subAgent.headOfficeAddress || '',
        contactPerson: subAgent.contactPerson || '',
        contactPhone: subAgent.contactPhone || '',
        contactEmail: subAgent.contactEmail || '',
        settlementAccount: subAgent.settlementAccount || '',
        accountTitle: subAgent.accountTitle || ''
      });
      // If subAgent has account title, show the field
      if (subAgent.accountTitle) {
        setShowAccountTitle(true);
        validateIban(subAgent.settlementAccount || '');
      }
    } else {
      setFormData({
        name: '',
        description: '',
        headOfficeAddress: '',
        contactPerson: '',
        contactPhone: '',
        contactEmail: '',
        settlementAccount: '',
        accountTitle: ''
      });
      setShowAccountTitle(false);
    }
    setErrors({});
    setFetchError(null);
  }, [subAgent, open]);
  // Remove the Pakistan prefix prefill effect — replace with empty effect
  useEffect(() => {
    // No auto-prefill for international format
  }, [open]);
  const handleChange = (e: React.ChangeEvent<HTMLInputElement | {
    name?: string;
    value: unknown;
  }>) => {
    const {
      name,
      value
    } = e.target;
    if (name) {
      // Special handling for settlementAccount (IBAN)
      if (name === 'settlementAccount') {
        // Format IBAN: remove spaces/dashes, convert to uppercase
        const formattedValue = String(value).replace(/[\s-]/g, '').toUpperCase();
        setFormData({
          ...formData,
          [name]: formattedValue,
          // Clear account title if IBAN is edited after a successful fetch
          ...(showAccountTitle && {
            accountTitle: ''
          })
        });
        // Validate the IBAN and update the button state
        validateIban(formattedValue);
        // If user changes IBAN after fetching title, reset the title
        if (showAccountTitle) {
          setShowAccountTitle(false);
          setFetchError(null);
        }
      } else {
        setFormData({
          ...formData,
          [name]: value
        });
      }
      // Clear error when field is edited
      if (errors[name as keyof FormErrors]) {
        setErrors({
          ...errors,
          [name]: undefined
        });
      }
    }
  };
  const handlePhoneFocus = () => {
    setPhoneFieldFocused(true);
  };
  const handlePhoneBlur = () => {
    setPhoneFieldFocused(false);
    // Validate phone number when field loses focus
    validatePhoneNumber(formData.contactPhone);
  };
  const validatePhoneNumber = (phone: string): boolean => {
    // Remove spaces for validation
    const cleanPhone = phone.replace(/\s+/g, '');
    // Check if it's a valid international phone number
    const isValid = PHONE_FORMAT.regex.test(cleanPhone);
    if (!isValid && cleanPhone) {
      setErrors({
        ...errors,
        contactPhone: 'International format required, e.g. +233 20 123 4567'
      });
    } else {
      // Clear error if valid or empty
      const updatedErrors = {
        ...errors
      };
      delete updatedErrors.contactPhone;
      setErrors(updatedErrors);
    }
    return isValid;
  };
  const validateIban = (iban: string): boolean => {
    // Check if it's a valid IBAN format
    const isValid = GENERAL_IBAN_REGEX.test(iban);
    setIbanValid(isValid);
    // Set error message if invalid
    if (iban && !isValid) {
      setErrors({
        ...errors,
        settlementAccount: 'Enter a valid IBAN (e.g., GB29NWBK60161331926819)'
      });
    } else {
      // Clear error if valid or empty
      const updatedErrors = {
        ...errors
      };
      delete updatedErrors.settlementAccount;
      setErrors(updatedErrors);
    }
    return isValid;
  };
  const handleFetchTitle = () => {
    setIsFetchingTitle(true);
    setFetchError(null);
    // Simulate API call with timeout
    setTimeout(() => {
      // 90% success rate for demo purposes
      const isSuccess = Math.random() < 0.9;
      if (isSuccess) {
        // Success case
        const mockTitles = ['PAGA GHANA LTD', 'MONEYGRAM NIGERIA PLC', 'WORLDREMIT KENYA LTD', 'FLUTTERWAVE GHANA LTD', 'M-PESA SAFARICOM KENYA', 'ORANGE MONEY SENEGAL SA', 'ACCRA MONEY CENTER LTD', 'LAGOS EXPRESS REMIT LTD', 'NAIROBI REMIT CENTER LTD'];
        const randomTitle = mockTitles[Math.floor(Math.random() * mockTitles.length)];
        setFormData({
          ...formData,
          accountTitle: randomTitle
        });
        setShowAccountTitle(true);
        console.log('Account title fetched successfully');
      } else {
        // Error case
        setFetchError('Title fetch failed. Please verify IBAN or try again.');
      }
      setIsFetchingTitle(false);
    }, 2500);
  };
  const validate = (): boolean => {
    const newErrors: FormErrors = {};
    if (!formData.name.trim()) {
      newErrors.name = 'Sub-Agent name is required';
    }
    if (!formData.headOfficeAddress.trim()) {
      newErrors.headOfficeAddress = 'Head Office Address is required';
    }
    if (!formData.contactPerson.trim()) {
      newErrors.contactPerson = 'Contact Person is required';
    }
    if (!formData.contactPhone.trim()) {
      newErrors.contactPhone = 'Contact Phone is required';
    } else {
      // Validate phone number
      const isValid = validatePhoneNumber(formData.contactPhone);
      if (!isValid) {
        newErrors.contactPhone = newErrors.contactPhone || errors.contactPhone;
      }
    }
    if (!formData.contactEmail.trim()) {
      newErrors.contactEmail = 'Contact Email is required';
    } else if (!EMAIL_REGEX.test(formData.contactEmail)) {
      newErrors.contactEmail = 'Contact Email is invalid';
    }
    if (!formData.settlementAccount.trim()) {
      newErrors.settlementAccount = 'Settlement Account is required';
    } else if (!validateIban(formData.settlementAccount)) {
      newErrors.settlementAccount = 'Enter a valid IBAN (e.g., GB29NWBK60161331926819)';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };
  const handleSubmit = () => {
    if (validate()) {
      onSave({
        ...formData,
        id: subAgent?.id
      });
    }
  };
  return <Drawer anchor="right" open={open} onClose={onClose} PaperProps={{
    sx: {
      width: {
        xs: '100%',
        sm: 400
      },
      p: 0
    }
  }} data-id="element-1928">
      <Box sx={{
      p: 3
    }} data-id="element-1929">
        <Box sx={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        mb: 3
      }} data-id="element-1930">
          <Typography variant="h6" data-id="element-1931">
            {subAgent ? 'Edit Sub Agent' : 'Add New Sub Agent'}
          </Typography>
          <IconButton onClick={onClose} size="small" data-id="element-1932">
            <XIcon size={18} data-id="element-1933" />
          </IconButton>
        </Box>
        <Box component="form" noValidate autoComplete="off" sx={{
        mt: 2
      }} data-id="element-1934">
          <FormControl fullWidth margin="normal" error={!!errors.name} required data-id="element-1935">
            <InputLabel id="sub-agent-name-label" data-id="element-1936">Sub-Agent Name</InputLabel>
            <Select labelId="sub-agent-name-label" name="name" value={formData.name} onChange={handleChange} label="Sub-Agent Name" data-id="element-1937">
              {VALID_SUB_AGENTS.map(subAgent => <MenuItem key={subAgent} value={subAgent} data-id="element-1938">
                  {subAgent}
                </MenuItem>)}
            </Select>
            {errors.name && <FormHelperText data-id="element-1939">{errors.name}</FormHelperText>}
          </FormControl>
          <TextField fullWidth margin="normal" label="Description" name="description" value={formData.description} onChange={handleChange} error={!!errors.description} helperText={errors.description} multiline rows={2} data-id="element-1940" />
          <TextField fullWidth margin="normal" label="Head Office Address" name="headOfficeAddress" value={formData.headOfficeAddress} onChange={handleChange} error={!!errors.headOfficeAddress} helperText={errors.headOfficeAddress} required multiline rows={2} data-id="element-1941" />
          <TextField fullWidth margin="normal" label="Contact Person" name="contactPerson" value={formData.contactPerson} onChange={handleChange} error={!!errors.contactPerson} helperText={errors.contactPerson} required data-id="element-1942" />
          {/* International Phone Input */}
          <TextField fullWidth margin="normal" label="Contact Phone" name="contactPhone" value={formData.contactPhone} onChange={handleChange} onFocus={handlePhoneFocus} onBlur={handlePhoneBlur} error={!!errors.contactPhone} helperText={errors.contactPhone || PHONE_FORMAT.helperText} required placeholder={PHONE_FORMAT.placeholder} data-id="element-1943" />
          <TextField fullWidth margin="normal" label="Contact Email" name="contactEmail" type="email" value={formData.contactEmail} onChange={handleChange} error={!!errors.contactEmail} helperText={errors.contactEmail} required placeholder="example@domain.com" data-id="element-1944" />
          {/* IBAN Settlement Account Field Group */}
          <Box sx={{
          mt: 3,
          mb: 2,
          display: 'flex',
          flexDirection: isMobile ? 'column' : 'row',
          alignItems: isMobile ? 'flex-start' : 'flex-end',
          gap: isMobile ? 2 : 1
        }} data-id="element-1945">
            <TextField fullWidth label="Settlement Account (IBAN)" name="settlementAccount" placeholder="e.g., GB29NWBK60161331926819" value={formData.settlementAccount} onChange={handleChange} error={!!errors.settlementAccount} helperText={errors.settlementAccount || 'Enter a valid IBAN'} required disabled={isFetchingTitle} InputProps={{
            sx: {
              textTransform: 'uppercase'
            }
          }} sx={{
            flex: isMobile ? 1 : 3,
            width: isMobile ? '100%' : 'auto'
          }} data-id="element-1946" />
            <Tooltip title="Validate and fetch account title" data-id="element-1947">
              <span data-id="element-1948">
                <Button variant="outlined" disabled={!ibanValid || isFetchingTitle} onClick={handleFetchTitle} sx={{
                minWidth: '120px',
                width: isMobile ? '100%' : 'auto',
                height: '56px',
                borderColor: '#D1D5DB',
                color: '#0EA5E9',
                '&:hover': {
                  borderColor: '#0EA5E9',
                  backgroundColor: 'rgba(14, 165, 233, 0.04)',
                  transform: 'translateY(-1px)'
                },
                '&.Mui-disabled': {
                  borderColor: '#E5E7EB'
                }
              }} aria-live="polite" data-id="element-1949">
                  {isFetchingTitle ? <>
                      <CircularProgress size={16} sx={{
                    mr: 1,
                    color: '#0EA5E9'
                  }} data-id="element-1950" />
                      Fetching...
                    </> : 'Fetch Title'}
                </Button>
              </span>
            </Tooltip>
          </Box>
          {/* Fetch error message */}
          {fetchError && <Fade in={!!fetchError} data-id="element-1951">
              <Alert severity="error" icon={<AlertCircleIcon size={18} data-id="element-1953" />} sx={{
            mb: 2
          }} onClose={() => setFetchError(null)} data-id="element-1952">
                {fetchError}
              </Alert>
            </Fade>}
          {/* Account Title Field (shown only after successful fetch) */}
          {showAccountTitle && <Fade in={showAccountTitle} data-id="element-1954">
              <TextField fullWidth margin="normal" label="Account Title" value={formData.accountTitle || ''} disabled InputProps={{
            readOnly: true,
            endAdornment: <InputAdornment position="end" data-id="element-1956">
                      <CheckCircleIcon size={18} color="#10B981" data-id="element-1957" />
                    </InputAdornment>
          }} sx={{
            mb: 2,
            '& .MuiInputBase-input.Mui-disabled': {
              WebkitTextFillColor: '#0F172A',
              fontWeight: 500
            },
            '& .MuiOutlinedInput-root.Mui-disabled .MuiOutlinedInput-notchedOutline': {
              borderColor: '#D1D5DB',
              borderStyle: 'dashed'
            }
          }} data-id="element-1955" />
            </Fade>}
        </Box>
      </Box>
      <Box sx={{
      position: 'sticky',
      bottom: 0,
      backgroundColor: 'background.paper',
      p: 3,
      borderTop: '1px solid #E5E7EB'
    }} data-id="element-1958">
        <Box sx={{
        display: 'flex',
        justifyContent: 'flex-end',
        gap: 2
      }} data-id="element-1959">
          <Button variant="outlined" onClick={onClose} data-id="element-1960">
            Cancel
          </Button>
          <Button variant="contained" onClick={handleSubmit} data-id="element-1961">
            Save
          </Button>
        </Box>
      </Box>
    </Drawer>;
};
export default SubAgentFormDrawer;